// ChatStart.jsx
'use strict';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';

import SearchInput from '../component/SearchInput.jsx';
import WithPageStatus from '../container/WithPageStatus.js';
import PullToRefresh from '../container/PullToRefresh.js';
import Spinning from '../style/Spinning.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import {
  headerHeight,
  color,
  textColor,
  breakpoint,
} from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';

import LoadingImage from '../../img/loading.png';
import BackIcon from '../../img/ic_topbar_back.svg';
import dropdownIcon from '../../img/btn-dropdown-white.svg';
import EmptyResultImg from '../../img/img-empty-nofollowing.svg';

import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';
import GridDiscoverUsers from '../container/GridDiscoverUsers.js';
import UserItem from '../container/UserItem.js';

const DROPDOWN_ITEM_HEIGHT = 48;

const ChatStart = React.memo(function ChatStart({
  category = null,
  query = '',
  via = '',
  itemIds = EMPTY_ARRAY,
  listSelectPath = EMPTY_ARRAY,
  isFirstPageFetching = true,
  isRemoteConfigMerged = false,
  isOnDesktop = false,
  swaggerOnlineFeed = null,
  swaggerNewFeed = null,
  setUserDiscoverCategory = NULL_FUNCTION,
  setUserDiscoverQuery = NULL_FUNCTION,
  searchUsers = NULL_FUNCTION,
  goBack = NULL_FUNCTION,
  subscribeFeedChannel = NULL_FUNCTION,
  unsubscribeFeedChannel = NULL_FUNCTION,
}) {
  const { t } = useTranslation();
  const [isInputFoucs, setIsInputFoucs] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const queryRef = useRef(query);
  queryRef.current = query;

  const fetchData = useCallback(
    ({ page }) => {
      const currentQuery = queryRef.current;
      if (!currentQuery) return;
      searchUsers({
        query: currentQuery,
        queryUrl: page === 1 ? undefined : page,
      });
    },
    [searchUsers]
  );

  useEffect(() => {
    if (!isRemoteConfigMerged) return;
    fetchData({ page: 1 });
  }, [isRemoteConfigMerged, category, fetchData]);

  useEffect(() => {
    if (category === swaggerOnlineFeed) {
      subscribeFeedChannel({ feedName: category });
    }
    return () => {
      if (category === swaggerOnlineFeed) {
        unsubscribeFeedChannel({ feedName: category });
      }
    };
  }, [
    category,
    swaggerOnlineFeed,
    subscribeFeedChannel,
    unsubscribeFeedChannel,
  ]);

  const setUserDiscoverCategoryRef = useRef(setUserDiscoverCategory);
  setUserDiscoverCategoryRef.current = setUserDiscoverCategory;
  const categoryRef = useRef(category);
  categoryRef.current = category;

  useEffect(() => {
    setUserDiscoverCategoryRef.current(categoryRef.current);
  }, []);

  const handleRefresh = useCallback(async () => {
    return searchUsers({ page: 1 });
  }, [searchUsers]);

  const handleSearchChange = useCallback(
    event => {
      const value = event.target.value?.replace(/[\b]/g, ' ');
      searchUsers({ query: value, page: 1, via });
      setUserDiscoverQuery(value);
    },
    [via, searchUsers, setUserDiscoverQuery]
  );

  const handleSearchFocus = () => {
    setIsInputFoucs(true);
  };

  const handleSearchBlur = () => {
    setIsInputFoucs(false);
  };

  const handleSearchClear = useCallback(() => {
    setIsInputFoucs(true);
    setUserDiscoverQuery('');
  }, [setUserDiscoverQuery]);

  const handleSearchCancel = useCallback(() => {
    setIsInputFoucs(false);
    setUserDiscoverQuery('');
  }, [setUserDiscoverQuery]);

  const handleFilterChange = useCallback(
    ({ category: cat }) => {
      setUserDiscoverCategory(cat);
      setIsOpened(false);
    },
    [setUserDiscoverCategory]
  );

  const shouldShowCancelButton = isInputFoucs || !!query;
  const dropdownFeedList = [swaggerOnlineFeed, swaggerNewFeed].filter(
    feed => feed
  );

  return (
    <StyledChatStart>
      {isOnDesktop ? (
        <DesktopHeader
          onBlur={e => {
            if (e.relatedTarget) return;
            setIsOpened(prev => !prev);
          }}
          tabIndex={0}
        >
          <DropdownButton onClick={() => setIsOpened(prev => !prev)}>
            <DropdownButtonText>
              {category === swaggerNewFeed
                ? t('discover_swagger_new', { ns: TranslationNamespace.FEED })
                : category === swaggerOnlineFeed
                  ? t('sort_recently_online', { ns: TranslationNamespace.FEED })
                  : t('discover_swagger_all', {
                      ns: TranslationNamespace.FEED,
                    })}
            </DropdownButtonText>
            <DropdownIcon
              src={dropdownIcon}
              isOpened={isOpened}
              alt="dropdown icon"
            />
            {isOpened && (
              <DesktopDropList>
                {dropdownFeedList.map(cat => (
                  <DesktopDropItem
                    key={cat}
                    value={
                      cat === swaggerNewFeed
                        ? t('discover_swagger_new', {
                            ns: TranslationNamespace.FEED,
                          })
                        : cat === swaggerOnlineFeed
                          ? t('sort_recently_online', {
                              ns: TranslationNamespace.FEED,
                            })
                          : t('discover_swagger_all', {
                              ns: TranslationNamespace.FEED,
                            })
                    }
                    onClick={() => handleFilterChange({ category: cat })}
                  >
                    {cat === swaggerNewFeed
                      ? t('discover_swagger_new', {
                          ns: TranslationNamespace.FEED,
                        })
                      : cat === swaggerOnlineFeed
                        ? t('sort_recently_online', {
                            ns: TranslationNamespace.FEED,
                          })
                        : t('discover_swagger_all', {
                            ns: TranslationNamespace.FEED,
                          })}
                  </DesktopDropItem>
                ))}
              </DesktopDropList>
            )}
          </DropdownButton>
        </DesktopHeader>
      ) : (
        <Header>
          {!isOnDesktop && (
            <GoBackButton
              data-element_id={ButtonId.All.ButtonBack}
              onClick={goBack}
            >
              <img src={BackIcon} alt="go back" />
            </GoBackButton>
          )}
          <DropdownButton onClick={() => !query && setIsOpened(prev => !prev)}>
            <DropdownButtonText>
              {query
                ? t('discover_swagger_all', { ns: TranslationNamespace.FEED })
                : category === swaggerNewFeed
                  ? t('discover_swagger_new', { ns: TranslationNamespace.FEED })
                  : category === swaggerOnlineFeed
                    ? t('sort_recently_online', {
                        ns: TranslationNamespace.FEED,
                      })
                    : t('discover_swagger_all', {
                        ns: TranslationNamespace.FEED,
                      })}
            </DropdownButtonText>
            {!query ? (
              <DropdownIcon
                src={dropdownIcon}
                isOpened={isOpened}
                alt="dropdown icon"
              />
            ) : null}
          </DropdownButton>
          <DropdownListWrapper
            listHeight={
              isOpened ? dropdownFeedList.length * DROPDOWN_ITEM_HEIGHT : 0
            }
          >
            {dropdownFeedList.map(cat => (
              <DropdownItem
                key={cat}
                value={
                  cat === swaggerNewFeed
                    ? t('discover_swagger_new', {
                        ns: TranslationNamespace.FEED,
                      })
                    : cat === swaggerOnlineFeed
                      ? t('sort_recently_online', {
                          ns: TranslationNamespace.FEED,
                        })
                      : t('discover_swagger_all', {
                          ns: TranslationNamespace.FEED,
                        })
                }
                onClick={() => handleFilterChange({ category: cat })}
              >
                {cat === swaggerNewFeed
                  ? t('discover_swagger_new', { ns: TranslationNamespace.FEED })
                  : cat === swaggerOnlineFeed
                    ? t('sort_recently_online', {
                        ns: TranslationNamespace.FEED,
                      })
                    : t('discover_swagger_all', {
                        ns: TranslationNamespace.FEED,
                      })}
              </DropdownItem>
            ))}
          </DropdownListWrapper>
        </Header>
      )}
      <PullToRefresh useWindow onRefresh={handleRefresh}>
        <Body id="main">
          {!isOnDesktop && (
            <SearchBarWrapper>
              <SearchBar>
                <SearchInput
                  value={query}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onSearchClear={handleSearchClear}
                />
              </SearchBar>
              {shouldShowCancelButton && (
                <CancelButton onClick={handleSearchCancel}>
                  {t('general_cancel', { ns: TranslationNamespace.GENERAL })}
                </CancelButton>
              )}
            </SearchBarWrapper>
          )}
          {query ? (
            itemIds.length === 0 ? (
              isFirstPageFetching ? (
                <SearchLoading src={LoadingImage} alt="loading" />
              ) : (
                <EmptyResult>
                  <NoResultImage src={EmptyResultImg} alt="empty icon" />
                  {t('search_no_result', { ns: TranslationNamespace.CHAT })}
                </EmptyResult>
              )
            ) : (
              <WithPageStatus path={listSelectPath}>
                {({ isNextPageFetching, nextPage }) => (
                  <InfiniteScroller
                    pageStart={1}
                    hasMore={nextPage != null}
                    loadMore={() => fetchData({ page: nextPage })}
                    isFetching={isNextPageFetching}
                    threshold={100}
                  >
                    <ChatDiscoverList>
                      <ScrollItem
                        loader={
                          <Loader>
                            <img src={LoadingImage} alt="loading" />
                          </Loader>
                        }
                      >
                        {itemIds.map(id => (
                          <UserItemWrapper key={id}>
                            <UserItem userId={id} query={query} />
                          </UserItemWrapper>
                        ))}
                      </ScrollItem>
                    </ChatDiscoverList>
                  </InfiniteScroller>
                )}
              </WithPageStatus>
            )
          ) : (
            <GridDiscoverUsersWrapper>
              <GridDiscoverUsers
                category={category}
                withChatLink
                size={104}
                marginTop={12}
              />
            </GridDiscoverUsersWrapper>
          )}
        </Body>
      </PullToRefresh>
    </StyledChatStart>
  );
});

ChatStart.propTypes = {
  category: PropTypes.string,
  query: PropTypes.string,
  via: PropTypes.string,
  itemIds: PropTypes.arrayOf(PropTypes.string),
  listSelectPath: PropTypes.arrayOf(PropTypes.string),
  isFirstPageFetching: PropTypes.bool,
  setUserDiscoverCategory: PropTypes.func,
  setUserDiscoverQuery: PropTypes.func,
  searchUsers: PropTypes.func,
  goBack: PropTypes.func,
  isRemoteConfigMerged: PropTypes.bool,
  isOnDesktop: PropTypes.bool,
  swaggerOnlineFeed: PropTypes.string,
  swaggerNewFeed: PropTypes.string,
  subscribeFeedChannel: PropTypes.func,
  unsubscribeFeedChannel: PropTypes.func,
};

const StyledChatStart = styled.div`
  padding-top: var(--space-to-pinned-notification);
  @media (max-width: ${breakpoint.mobile}px) {
    padding-top: calc(58px + var(--space-to-pinned-notification));
    padding-bottom: 48px; // set it to 48 based on design team's feedback (2024.02)
  }
`;

const Header = styled.div`
  position: fixed;
  top: var(--space-to-pinned-notification);
  left: 0;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${headerHeight}px;
  border-bottom: 1px solid ${color.neutral['30_alpha_15']};
  background-color: ${color.black};
  font-size: 17px;
  line-height: 22px;
  z-index: 3;
`;

const DesktopHeader = styled.div`
  margin-left: 29px;
  margin-top: 24px;
  width: max-content;
  position: relative;
`;

const DropdownButton = styled.button`
  ${ResetButtonStyle}
  flex: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${textColor.white};
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
`;

const DropdownIcon = styled.img.attrs(({ isOpened }) => ({
  style: {
    transform: `rotate(${isOpened ? '180' : '0'}deg)`,
  },
}))`
  margin-left: 4px;
  flex: none;
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
`;

const DropdownButtonText = styled.div`
  ${TextEllipsis}
`;

const DropdownListWrapper = styled.div.attrs(({ listHeight }) => ({
  style: {
    height: `${listHeight}px`,
  },
}))`
  position: absolute;
  top: calc(${headerHeight}px + var(--space-to-pinned-notification));
  left: 0;
  right: 0;
  z-index: 1;
  transition: height 0.3s;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  ${ResetButtonStyle}
  border-bottom: 1px solid ${color.neutral['30_alpha_50']};
  width: 100%;
  height: ${DROPDOWN_ITEM_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${textColor.black};
  font-size: 16px;
  font-weight: 600;
  background-color: ${color.grey[900]};
`;

const GoBackButton = styled.button`
  box-sizing: content-box;
  position: absolute;
  top: 0;
  left: 0;
  outline: none;
  height: 30px;
  width: auto;
  border: 0;
  background: none;
  padding: 14px 15px;
  cursor: pointer;

  :hover {
    opacity: 0.9;
  }

  & > img {
    height: 100%;
    vertical-align: top;
  }
`;

const DesktopDropList = styled.div`
  position: absolute;
  left: 16px;
  top: 28px;
  background-color: ${color.grey[900]};
  min-width: 160px;
  padding: 20px;
  margin: 0;
  z-index: 10;
  box-shadow:
    0 16px 16px 0 ${color.neutral['0_alpha_20']},
    0 0 16px 0 ${color.neutral['0_alpha_10']};
  border-radius: 8px;
`;

const DesktopDropItem = styled.button`
  ${ResetButtonStyle}
  :not(:last-child) {
    margin-bottom: 20px;
  }
  display: block;
  font-size: 14px;
  line-height: 1.43;
  color: ${textColor.grey};
  text-align: start;
  :hover {
    opacity: 0.7;
  }
`;

const Body = styled.div`
  flex: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  padding: 22px 16px 0;
`;

const SearchBar = styled.div`
  flex: auto;
`;

const ChatDiscoverList = styled.div`
  margin: 24px 16px;
`;

const UserItemWrapper = styled.div`
  margin-bottom: 20px;
`;

const CancelButton = styled.button`
  padding: 0 0 0 16px;
  flex: none;
  outline: none;
  border: none;
  background: none;
  cursor: pointer;
  color: ${textColor.white};
  user-select: none;
`;

const Loader = styled.div`
  text-align: center;
  padding-top: 10px;
  width: 100%;

  & > img {
    animation: 3s infinite ${Spinning};
  }
`;

const SearchLoading = styled.img`
  display: block;
  height: auto;
  margin: auto;
  margin-top: 50%;
  animation: 1.4s infinite ${Spinning};
`;

const NoResultImage = styled.img`
  margin-bottom: 12px;
  width: 120px;
  height: 120px;
  opacity: 0.5;
`;

const EmptyResult = styled.div`
  margin-top: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: ${textColor.black};
  font-size: 14px;
`;

const GridDiscoverUsersWrapper = styled.div`
  margin-top: 44px;
  @media (max-width: ${breakpoint.mobile}px) {
    margin-top: 20px;
  }
`;

export { ChatStart };
export default ChatStart;
